import { cn } from '@util/index';
import Link from 'next/link';
import { staticCategories } from '@c/navigation/utils/categories';

import Honda from '@i/brands/honda';
import Yamaha from '@i/brands/yamaha';
import Kawasaki from '@i/brands/kawasaki';
import KTM from '@i/brands/ktm';
import Suzuki from '@i/brands/suzuki';
import Husqvarna from 'i/brands/husqvarna';
import Gasgas from '@i/brands/gasgas';
import Beta from '@i/brands/beta';

type BrandIconProps = {
  brand: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
};

interface Brand {
  label: string;
  color: string;
}

const brands: Brand[] = [
  { label: 'honda', color: '#C00000' },
  { label: 'yamaha', color: '#303591' },
  { label: 'kawasaki', color: '#68A321' },
  { label: 'ktm', color: '#EB721E' },
  { label: 'gasgas', color: '#E5282A' },
  { label: 'suzuki', color: '#DEBD1E' },
  { label: 'husqvarna', color: '#1A3377' },
  { label: 'beta', color: '#E71A23' },
];

const TSX_BRAND_COMPONENTS = {
  honda: Honda,
  yamaha: Yamaha,
  kawasaki: Kawasaki,
  ktm: KTM,
  suzuki: Suzuki,
  husqvarna: Husqvarna,
  gasgas: Gasgas,
  beta: Beta,
} as const;

const bigSvgs = ['honda', 'kawasaki', 'gasgas'];

export default function ShopOemParts() {
  return (
    <div className="mt-16 grid h-full w-full grid-flow-col grid-rows-2 gap-[1.6rem] overflow-x-scroll scrollbar-none sm:auto-rows-auto sm:grid-cols-4 sm:overflow-x-auto">
      {brands.map((brand) => (
        <OemBrandCard key={brand.label} brand={brand} />
      ))}
    </div>
  );
}

const OemBrandCard = ({ brand }: { brand: Brand }) => {
  const brandLink = staticCategories
    .find((c) => c.label === 'Dirt Bikes')
    ?.sections?.find((s) => s.label === 'OEM Parts')
    ?.links?.find(
      (l) => l.label.toLowerCase().split(' ').join('') === brand.label
    );

  return (
    <Link
      href={brandLink?.href || ''}
      className="group group relative flex h-[20rem] w-[28rem] shrink-0 items-center justify-center overflow-hidden rounded-[2rem] bg-[#F6F6F6] p-6 transition-colors duration-75 ease-linear sm:w-full"
      target={brandLink?.href ? '_blank' : undefined}
      style={
        {
          '--brand-color': brand.color,
        } as React.CSSProperties
      }
    >
      <div className="absolute inset-0 rounded-[2rem] bg-[var(--brand-color)] opacity-0 transition-opacity duration-75 ease-linear group-hover:opacity-100" />
      <BrandIcon
        brand={brand.label}
        className={cn(
          'relative z-10 transition-transform duration-300 group-hover:scale-110 group-hover:invert',
          brand.label === 'beta'
            ? 'h-[7rem] w-auto'
            : bigSvgs.includes(brand.label)
            ? 'h-[2rem] w-auto'
            : 'h-[4rem] w-auto'
        )}
      />
    </Link>
  );
};

const BrandIcon = ({ brand, className, size = 'md' }: BrandIconProps) => {
  const normalizedBrand = brand.toLowerCase().replace(/\s+/g, '');

  if (normalizedBrand in TSX_BRAND_COMPONENTS) {
    const Component =
      TSX_BRAND_COMPONENTS[
        normalizedBrand as keyof typeof TSX_BRAND_COMPONENTS
      ];
    return <Component className={cn(className)} />;
  }

  return (
    <div
      className={cn(
        'flex items-center justify-center rounded bg-gray-200 text-xs font-medium text-gray-600',
        className
      )}
      title={brand}
    >
      {brand.slice(0, 3).toUpperCase()}
    </div>
  );
};
